/**
 * Однострочный ввод и многострочный текст. Спецификация — docs/04 §5.2.2.
 *
 * Вариант `seamless` существует ради карточки задачи: сорок полей в рамках
 * превращают карточку в сетку прямоугольников, поэтому граница появляется
 * только при наведении и фокусе.
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/cn";

const fieldVariants = cva(
  "w-full min-w-0 rounded-[var(--pt-radius-3)] text-[var(--pt-font-ui)] " +
    "text-[var(--pt-text-primary)] placeholder:text-[var(--pt-text-tertiary)] " +
    "transition-[background-color,border-color] duration-[var(--pt-dur-micro)] " +
    "focus:outline-none disabled:cursor-not-allowed disabled:text-[var(--pt-text-disabled)]",
  {
    variants: {
      variant: {
        outlined:
          "border border-[var(--pt-border-control)] bg-[var(--pt-bg-surface)] " +
          "hover:border-[var(--pt-border-strong)] focus:border-[var(--pt-focus-ring)] " +
          "focus:shadow-[var(--pt-ring)] disabled:border-[var(--pt-border-default)] " +
          "disabled:bg-[var(--pt-bg-sunken)]",
        seamless:
          "border border-transparent bg-transparent hover:border-[var(--pt-border-control)] " +
          "focus:border-[var(--pt-focus-ring)] focus:bg-[var(--pt-bg-surface)] " +
          "focus:shadow-[var(--pt-ring)]",
        filled:
          "border border-transparent bg-[var(--pt-bg-sunken)] " +
          "focus:border-[var(--pt-focus-ring)] focus:shadow-[var(--pt-ring)]",
      },
      size: {
        sm: "h-[var(--pt-control-h-sm)] px-[var(--pt-space-6)]",
        md: "h-[var(--pt-control-h-md)] px-[var(--pt-pad-x)]",
        lg: "h-[var(--pt-control-h-lg)] px-[var(--pt-space-12)]",
      },
      invalid: {
        true: "border-[var(--pt-danger-border)] focus:border-[var(--pt-danger-border)]",
      },
    },
    defaultVariants: { variant: "outlined", size: "md" },
  },
);

interface FieldFrameProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /**
   * Резервировать место под сообщение: появление ошибки не должно двигать форму.
   *
   * По умолчанию не резервируем. Дизайн-система просит это место «в формах, где
   * ошибки вероятны», и таких форм в системе одна — вход. У соседей по строке,
   * `Select` и `Combobox`, места под сообщение нет вовсе, поэтому поле с ним
   * вставало на строку выше выпадающего списка, а кнопка рядом — на двадцать
   * пикселей ниже поля: выравнивание по нижнему краю равняло невидимую строку.
   */
  reserveErrorSpace?: boolean;
  htmlFor: string;
  children: React.ReactNode;
  className?: string;
}

function FieldFrame({
  label,
  hint,
  error,
  required,
  reserveErrorSpace = false,
  htmlFor,
  children,
  className,
}: FieldFrameProps): React.ReactElement {
  return (
    <div className={cn("flex flex-col", className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="mb-[var(--pt-space-4)] text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] font-medium text-[var(--pt-text-secondary)]"
        >
          {label}
          {required && (
            <span aria-hidden className="ml-[2px] text-[var(--pt-danger-text)]">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {(error || hint || reserveErrorSpace) && (
        <p
          id={error ? `${htmlFor}-error` : hint ? `${htmlFor}-hint` : undefined}
          className={cn(
            "mt-[var(--pt-space-4)] text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)]",
            error ? "text-[var(--pt-danger-text)]" : "text-[var(--pt-text-tertiary)]",
            !error && !hint && "invisible",
          )}
        >
          {error ?? hint ?? " "}
        </p>
      )}
    </div>
  );
}

export interface TextFieldProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof fieldVariants> {
  label?: string;
  hint?: string;
  error?: string;
  leadingIcon?: React.ReactNode;
  /** Кнопка очистки: появляется при непустом значении. */
  clearable?: boolean;
  onClear?: () => void;
  containerClassName?: string;
  /** Полоса под полем под будущее сообщение об ошибке.
   *
   * По умолчанию её нет: она нужна там, где ошибки вероятны и их появление
   * не должно двигать форму, — то есть на входе. В остальных местах она
   * поднимала поле относительно соседних селектов, у которых её не бывает,
   * и опускала кнопку рядом на двадцать пикселей. */
  reserveErrorSpace?: boolean;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    id,
    label,
    hint,
    error,
    variant,
    size,
    leadingIcon,
    clearable = false,
    onClear,
    className,
    containerClassName,
    required,
    reserveErrorSpace,
    value,
    ...props
  },
  ref,
) {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const hasValue = value !== undefined && value !== "";

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      reserveErrorSpace={reserveErrorSpace}
      htmlFor={fieldId}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        {leadingIcon && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-[var(--pt-space-6)] flex text-[var(--pt-text-tertiary)]"
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          value={value}
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          className={cn(
            fieldVariants({ variant, size, invalid: error ? true : undefined }),
            leadingIcon && "pl-[26px]",
            clearable && hasValue && "pr-[26px]",
            className,
          )}
          {...props}
        />
        {clearable && hasValue && (
          <button
            type="button"
            aria-label="Очистить поле"
            onClick={onClear}
            className="absolute right-[var(--pt-space-4)] flex size-[16px] items-center justify-center rounded-[var(--pt-radius-2)] text-[var(--pt-text-tertiary)] hover:bg-[var(--pt-bg-hover)] hover:text-[var(--pt-text-primary)]"
          >
            <X className="size-[12px]" />
          </button>
        )}
      </div>
    </FieldFrame>
  );
});

export interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof fieldVariants> {
  label?: string;
  hint?: string;
  error?: string;
  containerClassName?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { id, label, hint, error, variant, className, containerClassName, required, rows = 4, ...props },
  ref,
) {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={fieldId}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        className={cn(
          fieldVariants({ variant, invalid: error ? true : undefined }),
          "h-auto resize-y py-[var(--pt-pad-y)] px-[var(--pt-pad-x)]",
          "leading-[var(--pt-lh-base)]",
          className,
        )}
        {...props}
      />
    </FieldFrame>
  );
});

export { fieldVariants };
